import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type Player, type PitchCount, type Game } from '../../services/api';

@customElement('game-player-manager')
export class GamePlayerManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String })
  gameId = '';

  @state()
  private game: Game | null = null;

  @state()
  private gamePlayers: PitchCount[] = [];

  @state()
  private availablePlayers: Player[] = [];

  @state()
  private loading = true;

  @state()
  private adding = false;

  @state()
  private removingPlayerId: string | null = null;

  @state()
  private showAddForm = false;

  @state()
  private selectedPlayerId = '';

  @state()
  private searchQuery = '';

  // Toast notifications
  @state()
  private toast: { type: 'success' | 'error'; message: string } | null = null;

  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated() {
    if (this.gameId) {
      this.loadData();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('gameId') && this.gameId && changedProps.get('gameId') !== undefined) {
      this.loadData();
    }
  }

  private async loadData() {
    if (!this.gameId) return;

    this.loading = true;
    try {
      const [game, pitches, players] = await Promise.all([
        api.getGame(this.gameId),
        api.getGamePitches(this.gameId),
        api.getPlayers(),
      ]);

      this.game = game;
      this.gamePlayers = pitches || [];

      // Filter out players already in the game
      const gamePlayerIds = new Set((pitches || []).map(p => p.player_id));
      this.availablePlayers = (players || []).filter(
        p => !gamePlayerIds.has(p.id) && !p.is_adhoc
      );
    } catch (error) {
      console.error('Failed to load data:', error);
      this.showToast('error', 'Failed to load game data');
    } finally {
      this.loading = false;
    }
  }

  private showToast(type: 'success' | 'error', message: string) {
    this.toast = { type, message };
    setTimeout(() => {
      this.toast = null;
    }, 3000);
  }

  private openAddForm() {
    this.showAddForm = true;
    this.selectedPlayerId = '';
    this.searchQuery = '';
  }

  private closeAddForm() {
    this.showAddForm = false;
    this.selectedPlayerId = '';
    this.searchQuery = '';
  }

  private async addPlayer() {
    if (!this.selectedPlayerId || !this.gameId) return;

    this.adding = true;
    try {
      const newPitch = await api.addPlayerToGame(this.gameId, this.selectedPlayerId);

      // Find the player info
      const player = this.availablePlayers.find(p => p.id === this.selectedPlayerId);

      // Add to game players list
      if (newPitch) {
        this.gamePlayers = [...this.gamePlayers, {
          ...newPitch,
          player_name: player?.name,
          jersey_number: player?.jersey_number,
        }];
      }

      // Remove from available list
      this.availablePlayers = this.availablePlayers.filter(
        p => p.id !== this.selectedPlayerId
      );

      this.showToast('success', `Added ${player?.name || 'player'} to game`);
      this.closeAddForm();
    } catch (error) {
      console.error('Failed to add player:', error);
      this.showToast('error', 'Failed to add player to game');
    } finally {
      this.adding = false;
    }
  }

  private async removePlayer(pitch: PitchCount) {
    if (!confirm(`Remove ${pitch.player_name || 'this player'} from the game? Their pitch count (${pitch.count}) will be deleted.`)) {
      return;
    }

    this.removingPlayerId = pitch.player_id;
    try {
      await api.removePlayerFromGame(this.gameId, pitch.player_id);

      // Remove from game players
      this.gamePlayers = this.gamePlayers.filter(p => p.player_id !== pitch.player_id);

      // Add back to available players (reload to get full player info)
      await this.loadData();

      this.showToast('success', `Removed ${pitch.player_name || 'player'} from game`);
    } catch (error) {
      console.error('Failed to remove player:', error);
      this.showToast('error', 'Failed to remove player from game');
    } finally {
      this.removingPlayerId = null;
    }
  }

  private get filteredAvailablePlayers(): Player[] {
    if (!this.searchQuery) return this.availablePlayers;

    const query = this.searchQuery.toLowerCase();
    return this.availablePlayers.filter(
      p => p.name.toLowerCase().includes(query) ||
           p.team_name?.toLowerCase().includes(query) ||
           p.jersey_number?.toString().includes(query)
    );
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex justify-center py-8">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;
    }

    if (!this.game) {
      return html`
        <div class="alert alert-warning">
          <span>Game not found</span>
        </div>
      `;
    }

    return html`
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div>
            <h3 class="text-lg font-semibold">Game Lineup</h3>
            <p class="text-sm text-base-content/70">
              ${this.game.name} • ${this.gamePlayers.length} player(s)
            </p>
          </div>
          <button
            class="btn btn-primary btn-sm w-full sm:w-auto"
            @click=${this.openAddForm}
            ?disabled=${this.availablePlayers.length === 0}
          >
            Add Player
          </button>
        </div>

        <!-- Add player form -->
        ${this.showAddForm ? this.renderAddForm() : nothing}

        <!-- Current players in game -->
        ${this.renderPlayersList()}

        <!-- Empty state -->
        ${this.gamePlayers.length === 0 ? html`
          <div class="text-center py-8 text-base-content/70">
            <p>No players in this game yet.</p>
            <p class="text-sm mt-2">Add players to track their pitch counts.</p>
          </div>
        ` : nothing}

        <!-- Toast notification -->
        ${this.toast ? html`
          <div class="toast toast-end">
            <div class="alert ${this.toast.type === 'success' ? 'alert-success' : 'alert-error'}">
              <span>${this.toast.message}</span>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private renderAddForm() {
    const filtered = this.filteredAvailablePlayers;

    return html`
      <div class="card bg-base-200">
        <div class="card-body py-4">
          <h4 class="font-semibold mb-2">Add Player to Game</h4>

          <!-- Search input -->
          <div class="form-control mb-2">
            <input
              type="text"
              class="input input-bordered input-sm"
              placeholder="Search by name, team, or number..."
              .value=${this.searchQuery}
              @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
            />
          </div>

          <!-- Player selection -->
          <div class="max-h-48 overflow-y-auto border border-base-300 rounded-lg">
            ${filtered.length > 0 ? filtered.map(player => html`
              <label
                class="flex items-center gap-3 p-2 hover:bg-base-300 cursor-pointer ${this.selectedPlayerId === player.id ? 'bg-primary/10' : ''}"
              >
                <input
                  type="radio"
                  name="player"
                  class="radio radio-sm radio-primary"
                  .checked=${this.selectedPlayerId === player.id}
                  @change=${() => this.selectedPlayerId = player.id}
                />
                <span class="font-mono text-sm text-base-content/70 w-8">
                  ${player.jersey_number ?? '-'}
                </span>
                <span class="flex-1 font-medium">${player.name}</span>
                <span class="text-sm text-base-content/70">${player.team_name || 'No team'}</span>
              </label>
            `) : html`
              <p class="p-4 text-center text-base-content/70">
                ${this.searchQuery ? 'No players match your search' : 'No more players available'}
              </p>
            `}
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-2 mt-3">
            <button class="btn btn-ghost btn-sm" @click=${this.closeAddForm}>
              Cancel
            </button>
            <button
              class="btn btn-primary btn-sm"
              @click=${this.addPlayer}
              ?disabled=${!this.selectedPlayerId || this.adding}
            >
              ${this.adding
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : 'Add to Game'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderPlayersList() {
    if (this.gamePlayers.length === 0) return nothing;

    return html`
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th class="text-center">Pitches</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.gamePlayers.map(pitch => html`
              <tr>
                <td class="font-mono text-base-content/70">${pitch.jersey_number ?? '-'}</td>
                <td class="font-semibold">${pitch.player_name || 'Unknown'}</td>
                <td class="text-center">
                  <span class="badge badge-lg font-mono">${pitch.count}</span>
                </td>
                <td class="text-right">
                  <button
                    class="btn btn-ghost btn-xs text-error"
                    @click=${() => this.removePlayer(pitch)}
                    ?disabled=${this.removingPlayerId === pitch.player_id}
                  >
                    ${this.removingPlayerId === pitch.player_id
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : 'Remove'}
                  </button>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }
}
